/**
 * Paddle.js overlay checkout, used when the API answers `{ provider: 'paddle', transactionId }`
 * (apps/api WEB_BILLING_PROVIDER=paddle). The server creates the transaction and sets its
 * custom_data; the browser only opens the overlay on that id, so nothing here can choose a price.
 *
 * Loaded from cdn.paddle.com on first use, so the Stripe path never fetches it.
 */

const PADDLE_JS_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js'

type PaddleEvent = { name?: string; data?: unknown }
type PaddleGlobal = {
  Initialize: (options: { token: string; eventCallback?: (event: PaddleEvent) => void }) => void
  Checkout: {
    open: (options: {
      transactionId: string
      customer?: { email: string }
      settings?: { displayMode?: 'overlay'; successUrl?: string; variant?: 'one-page' | 'multi-page'; locale?: string }
    }) => void
  }
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal
  }
}

let loading: Promise<PaddleGlobal> | null = null
let initializedToken: string | null = null
// The single event handler for the page: each open() installs the one that matters to it.
let onCheckoutEvent: ((event: PaddleEvent) => void) | null = null

/**
 * Paddle.js's client-side token for the live account. Public by design (every checkout page ships it),
 * so it has a default here: app.itsbaba.com is built by Cloudflare's git integration, whose build
 * variables live outside Infisical. VITE_PADDLE_CLIENT_TOKEN still overrides it.
 */
const LIVE_PADDLE_CLIENT_TOKEN = 'live_a45451a87d773844393c4a0da1d'

export function paddleClientToken(): string | undefined {
  const token = import.meta.env.VITE_PADDLE_CLIENT_TOKEN
  return typeof token === 'string' && token.trim() ? token.trim() : LIVE_PADDLE_CLIENT_TOKEN
}

function loadPaddleJs(): Promise<PaddleGlobal> {
  if (typeof window === 'undefined') return Promise.reject(new Error('Paddle.js needs a browser'))
  if (window.Paddle) return Promise.resolve(window.Paddle)
  if (loading) return loading
  loading = new Promise<PaddleGlobal>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = PADDLE_JS_URL
    script.async = true
    script.onload = () => (window.Paddle ? resolve(window.Paddle) : reject(new Error('Paddle.js did not load')))
    script.onerror = () => {
      loading = null
      reject(new Error('Paddle.js failed to load'))
    }
    document.head.appendChild(script)
  })
  return loading
}

/**
 * Opens the overlay for a server-created transaction. Paddle.js sends the buyer to `successUrl`
 * itself when payment completes (the same success page Stripe returns to), so `onCompleted` is only
 * for the caller's own bookkeeping; `onClosed` runs when the buyer dismisses the overlay unpaid.
 */
export async function openPaddleCheckout(options: {
  transactionId: string
  email?: string | null
  successUrl: string
  locale?: string
  onCompleted?: () => void
  onClosed?: () => void
}): Promise<void> {
  const token = paddleClientToken()
  if (!token) throw new Error('Paddle checkout is not configured')
  const paddle = await loadPaddleJs()
  let completed = false
  onCheckoutEvent = (event) => {
    if (event.name === 'checkout.completed') {
      completed = true
      options.onCompleted?.()
    } else if (event.name === 'checkout.closed' && !completed) {
      options.onClosed?.()
    }
  }
  if (initializedToken !== token) {
    paddle.Initialize({ token, eventCallback: (event) => onCheckoutEvent?.(event) })
    initializedToken = token
  }
  paddle.Checkout.open({
    transactionId: options.transactionId,
    ...(options.email ? { customer: { email: options.email } } : {}),
    settings: { displayMode: 'overlay', successUrl: options.successUrl, ...(options.locale ? { locale: options.locale } : {}) },
  })
}
